"use client"

import { useState, useEffect } from "react"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  ArrowLeft,
  Share2,
  Heart,
  ShoppingCart,
  Phone,
  Mail,
  CheckCircle,
  Truck,
  Shield,
  Eye,
  Play,
} from "lucide-react"
import TextReveal from "@/components/animated/text-reveal"
import FloatingCard from "@/components/animated/floating-card"
import GradientBackground from "@/components/animated/gradient-background"
import Breadcrumbs from "@/components/seo/breadcrumbs"
import ProductShowcase from "@/components/3d/product-showcase"
import ProductVideo from "@/components/video/product-video"
import { businessInfo } from "@/lib/metadata"
import { products } from "@/data/products"

interface Product {
  id: number
  name: string
  category: string | null
  subcategory: string | null
  image: string
  alt: string
  meta: {
    title: string
    description: string
    keywords: string
  }
}

interface ProductDetailClientProps {
  product: Product
}

export default function ProductDetailClient({ product }: ProductDetailClientProps) {
  const router = useRouter()
  const [isMobile, setIsMobile] = useState(false)
  const [isLiked, setIsLiked] = useState(false)
  const [showVideo, setShowVideo] = useState(false)
  const [show3D, setShow3D] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])


  // Defensive: fallback if product or its properties are missing
  const relatedProducts = product && product.category
    ? products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4)
    : [];

  const safeCategory = product?.category ? String(product.category) : "unknown";
  const safeName = product?.name ? String(product.name) : "unknown";
  const safeId = product?.id ? product.id : "";

  const breadcrumbItems = [
    { name: "Products", url: "/products" },
    { name: safeCategory, url: `/categories/${safeCategory.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}` },
    { name: safeName, url: `/products/${safeName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}-${safeId}` },
  ];

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: product.meta.description,
          url: window.location.href,
        })
      } catch (err) {
        console.log("Error sharing:", err)
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href)
    }
  }

  return (
    <div className="min-h-screen relative">
      <GradientBackground />

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          <Button variant="ghost" asChild className="hover:bg-white/50">
            <Link href="/products">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Products
            </Link>
          </Button>
        </motion.div>

        {/* Breadcrumbs */}
        <Breadcrumbs items={breadcrumbItems} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Product Images/Media */}
          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg overflow-hidden">
              <CardContent className="p-0">
                <div className="relative aspect-square">
                  {showVideo ? (
                    <ProductVideo productType={product.category ?? ""} />
                  ) : show3D ? (
                    <ProductShowcase productType={product.category ?? ""} />
                  ) : (
                    <Image
                      src={
                        product.image ||
                        `/placeholder.svg?height=600&width=600&text=${encodeURIComponent(product.name)}`
                      }
                      alt={product.alt}
                      fill
                      className="object-cover"
                      priority
                    />
                  )}

                  {/* Media Controls */}
                  <div className="absolute top-4 right-4 flex space-x-2">
                    {/* <motion.button
                      className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg"
                      onClick={() => setShowVideo(!showVideo)}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Play className="h-4 w-4 text-slate-700" />
                    </motion.button> */}
                    {/* <motion.button
                      className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg"
                      onClick={() => setShow3D(!show3D)}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Eye className="h-4 w-4 text-slate-700" />
                    </motion.button> */}
                  </div>

                  {/* Category Badge */}
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-white/90 text-slate-900 backdrop-blur-sm">{product.category}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Product Details */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <div>
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">{product.name}</h1>
                  {product.subcategory && (
                    <Badge variant="outline" className="mb-4">
                      {product.subcategory}
                    </Badge>
                  )}
                </div>
                <div className="flex space-x-2">
                  <motion.button
                    className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                    onClick={() => setIsLiked(!isLiked)}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Heart className={`h-5 w-5 ${isLiked ? "fill-red-500 text-red-500" : "text-slate-600"}`} />
                  </motion.button>
                  <motion.button
                    className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                    onClick={handleShare}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Share2 className="h-5 w-5 text-slate-600" />
                  </motion.button>
                </div>
              </div>

              <p className="text-lg text-slate-600 leading-relaxed mb-6">{product.meta.description}</p>

              {/* Features */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {[
                  { icon: CheckCircle, title: "Quality Assured", desc: "Meets industry standards" },
                  { icon: Truck, title: "Fast Delivery", desc: "Quick shipping available" },
                  { icon: Shield, title: "Warranty", desc: "Manufacturer warranty" },
                ].map((feature, index) => (
                  <Card key={feature.title} className="bg-slate-50/50 border-0">
                    <CardContent className="p-4 text-center">
                      <feature.icon className="h-8 w-8 text-slate-700 mx-auto mb-2" />
                      <h3 className="font-semibold text-sm mb-1">{feature.title}</h3>
                      <p className="text-xs text-slate-600">{feature.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.div className="flex-1" whileTap={{ scale: 0.98 }}>
                  <Button
                    size="lg"
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    onClick={() => router.push(`/contact?product=${encodeURIComponent(product.name)}`)}
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Request Quote
                  </Button>
                </motion.div>
                <motion.div whileTap={{ scale: 0.98 }}>
                  <Button size="lg" variant="outline" asChild>
                    <Link href={`tel:${businessInfo.telephone}`}>
                      <Phone className="h-5 w-5 mr-2" />
                      Call Now
                    </Link>
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Product Specifications */}
        <FloatingCard delay={0.2}>
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg mb-16">
            <CardHeader>
              <CardTitle>Product Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-lg mb-4">Specifications</h3>
                  <div className="space-y-3">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-slate-500 text-sm min-w-[90px]">Category:</span>
                      <span className="font-medium text-slate-900">{product.category ?? "—"}</span>
                    </div>
                    {product.subcategory && (
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-slate-500 text-sm min-w-[90px]">Type:</span>
                        <span className="font-medium text-slate-900">{product.subcategory}</span>
                      </div>
                    )}
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-slate-500 text-sm min-w-[90px]">Availability:</span>
                      <span className="font-medium text-green-600">In Stock</span>
                    </div>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-slate-500 text-sm min-w-[90px]">Delivery:</span>
                      <span className="font-medium text-slate-900">1-3 Business Days</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-4">Applications</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li>• Industrial construction projects</li>
                    <li>• Commercial building applications</li>
                    <li>• Infrastructure development</li>
                    <li>• Manufacturing facilities</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </FloatingCard>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section>
            <TextReveal>
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Related Products</h2>
                <p className="text-lg text-slate-600">More products in the {product.category} category</p>
              </div>
            </TextReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct, index) => (
                <FloatingCard key={relatedProduct.id} delay={index * 0.1}>
                  <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-2xl transition-all duration-500">
                    <CardContent className="p-0">
                      <Link
                        href={`/products/${relatedProduct.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}-${relatedProduct.id}`}
                      >
                        <div className="relative aspect-square overflow-hidden rounded-t-lg">
                          <Image
                            src={
                              relatedProduct.image ||
                              `/placeholder.svg?height=300&width=300&text=${encodeURIComponent(relatedProduct.name)}`
                            }
                            alt={relatedProduct.alt}
                            fill
                            className="object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-lg mb-2 line-clamp-2">{relatedProduct.name}</h3>
                          <p className="text-slate-600 text-sm line-clamp-2 mb-3">{relatedProduct.meta.description}</p>
                          <Badge variant="outline" className="text-xs">
                            {relatedProduct.category}
                          </Badge>
                        </div>
                      </Link>
                    </CardContent>
                  </Card>
                </FloatingCard>
              ))}
            </div>

            <div className="text-center mt-8">
              <Button asChild variant="outline" size="lg">
                <Link href="/products">View All Products</Link>
              </Button>
            </div>
          </section>
        )}

        {/* Mobile bottom spacing */}
        {isMobile && <div className="h-20" />}
      </div>
    </div>
  )
}
